import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from '../auth/apiFetch';
import { getToken } from '../auth/PasswordGate';

export interface PlaidAccount {
  id: number;
  plaid_account_id: string;
  persistent_account_id: string | null;
  name: string;
  mask: string | null;
  type: string | null;
  subtype: string | null;
}

export interface ManualImport {
  id: number;
  filename: string | null;
  kind: string;
  importedAt: string;
  rowCount: number;
  insertedCount: number;
  mergedCount: number;
  // Live row count currently linked to this import. Differs from
  // insertedCount once a later import reassigns overlapping rows. Drops to
  // 0 when every row has been claimed by a newer import — the signal that
  // this import is safe to delete without losing transactions.
  currentCount: number;
  earliestDate: string | null;
  latestDate: string | null;
}

export interface PlaidItem {
  id: number;
  /** 'plaid' for OAuth-linked banks, 'manual' for CSV-imported sources. */
  source: 'plaid' | 'manual';
  institutionName: string | null;
  status: string;
  lastSyncedAt: string | null;
  createdAt: string;
  accounts: PlaidAccount[];
  /** Populated only when source === 'manual'. Each entry is one CSV upload. */
  imports: ManualImport[];
}

export type PlaidItemsState =
  | { status: 'loading'; items: []; householdHasItems: false; householdInstitutionNames: [] }
  | {
      status: 'ready';
      items: PlaidItem[];
      householdHasItems: boolean;
      householdInstitutionNames: string[];
      reload: () => void;
    }
  | {
      status: 'error';
      items: [];
      householdHasItems: false;
      householdInstitutionNames: [];
      error: string;
      reload: () => void;
    };

const PlaidItemsContext = createContext<PlaidItemsState | null>(null);

// Single shared state for the household's Plaid items so a reload triggered
// anywhere (auto-sync after /api/plaid/sync, manual Sync now, disconnect)
// propagates to every subscriber — Settings, Layout nav, etc.
export function PlaidItemsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PlaidItemsState>({
    status: 'loading',
    items: [],
    householdHasItems: false,
    householdInstitutionNames: [],
  });

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setState({
        status: 'ready',
        items: [],
        householdHasItems: false,
        householdInstitutionNames: [],
        reload: load,
      });
      return;
    }
    apiFetch('/api/plaid/items')
      .then(async (res) => {
        if (!res.ok) throw new Error(`items ${res.status}`);
        return (await res.json()) as {
          items: PlaidItem[];
          householdHasItems: boolean;
          householdInstitutionNames: string[];
        };
      })
      .then((data) => {
        setState({
          status: 'ready',
          items: data.items,
          householdHasItems: data.householdHasItems,
          householdInstitutionNames: data.householdInstitutionNames ?? [],
          reload: load,
        });
      })
      .catch((err: Error) => {
        setState({
          status: 'error',
          items: [],
          householdHasItems: false,
          householdInstitutionNames: [],
          error: err.message,
          reload: load,
        });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return <PlaidItemsContext.Provider value={state}>{children}</PlaidItemsContext.Provider>;
}

export function usePlaidItems(): PlaidItemsState {
  const ctx = useContext(PlaidItemsContext);
  if (!ctx) throw new Error('usePlaidItems must be used within PlaidItemsProvider');
  return ctx;
}
